import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactRequest } from '../contact-request';
import { ContactRequestComment } from '../contact-request-comment';
import { ContactRequestSource } from '../contact-request-source';
import { Country } from '../country';
import { InterestLevel } from '../interest-level';
import { Language } from '../language';
import { School } from '../school';
import * as moment from 'moment';
import { HttpService } from '../service/http.service';
import { Router } from '@angular/router';
import { ContactRequestService } from '../service/contact-request.service';
import { ToastMessageService } from '../toast-message.service';
import { GmailMessage } from '../gmail-message';
import { CKEDITOREMAILCONFIG } from '../ckeditor-config';
import { LeadEmailTemplate } from '../lead-email-template';
import { GmailService } from '../service/gmail.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ContactRequestQuotation } from '../contact-request-quotation';
import { AttachmentQuotation } from '../attachment-quotation';
import { Observable } from 'rxjs';
import { AppointmentType } from '../appointment-type';
import { RoomType } from '../room-type';
import { AccommodationType } from '../accommodation-type';
import { BoardType } from '../board-type';
import { ContactRequestEmailHistory } from '../contact-request-email-history';
import { EmailSignature } from '../email-signature';
import { ValidatorConstants } from 'src/global/validator-constants';
import { ApiError } from '../api-error';
import * as Editor from '../../libs/ckeditor';
import { SimpleSchoolWithCountries } from '../simple-school-with-countries';

@Component({
  selector: 'app-edit-contact-request',
  templateUrl: './edit-contact-request.component.html',
  styleUrls: ['./edit-contact-request.component.css']
})
export class EditContactRequestComponent implements OnInit {

  comments: ContactRequestComment[] = [];
  commentForm!: FormGroup;
  contactRequestForm!: FormGroup;
  requestInformationForm!: FormGroup;
  noTokenError = false;
  display = false;

  selectedDate!: Date;
  interestLevels: InterestLevel[] = [];
  selectedInterestLevel: InterestLevel | null = null;

  displayMailingDialog = false;
  emailForm!: FormGroup;
  emailEditorConfig = CKEDITOREMAILCONFIG;
  displayQuotationSelection = false;
  quotations: ContactRequestQuotation[] = [];
  selectedQuotation!: ContactRequestQuotation | null;

  emailTemplates: LeadEmailTemplate[] = [];
  selectedEmailTemplate!: LeadEmailTemplate;

  attachmentQuotations: AttachmentQuotation[] = [];
  attachmentLoading = false;

  emailSending = false;

  sourceType: ContactRequestSource | null = null;

  accommodationTypes: AccommodationType[] = [];
  roomTypes: RoomType[] = [];
  boardTypes: BoardType[] = [];

  emailHistories: ContactRequestEmailHistory[] = [];
  selectedEmailHistory!: ContactRequestEmailHistory;

  travelYearRange = moment().year() + ":" + moment().add(5, "years").year();
  birthYearRange = moment().subtract(90, "years").year() + ":" + moment().subtract(7, "years").year();

  appointmentTypes: AppointmentType[] = [];
  appointmentTypesObs!: Observable<AppointmentType[]>;

  displayMailHistoryDetail = false;

  public Editor = Editor;

  @Input("requestId")
  requestId!: number;

  @Output()
  selectedRequestEmitter = new EventEmitter<ContactRequest>();

  proceedActions = [
    { label: "Processed", value: true },
    { label: "Not processed", value: false }
  ];

  constructor(
    private httpService: HttpService,
    private builder: FormBuilder,
    private router: Router,
    public contactRequestService: ContactRequestService,
    private toastService: ToastMessageService,
    private domSanitizer: DomSanitizer,
    private gmailService: GmailService
  ) { }

  ngOnInit(): void {
    this.appointmentTypesObs = this.httpService.getAllAppointmentTypes();
    this.appointmentTypesObs.subscribe(res => this.appointmentTypes = res);
    this.httpService.getContactRequestEmailHistoryByRequestId(this.contactRequestService.contactRequest!.id!).subscribe(res => {
      for(let history of res){
        history.email.sentDateTime = moment(history.email.sentDateTime);
        if(history.email.openedAt){
          history.email.openedAt = moment(history.email.openedAt);
        }
      }
      this.emailHistories = res;
    });

    this.sourceType = this.contactRequestService.contactRequest!.source;
    this.contactRequestService.leadType = this.contactRequestService.contactRequest!.leadType;
    this.updateComments();
    this.initContactRequestForm();
    this.initCommentForm();
    this.selectedInterestLevel = this.contactRequestService.contactRequest!.interestLevel;
    this.selectedRequestEmitter.emit(this.contactRequestService.contactRequest!);


    this.httpService.getSalesAgents().subscribe(agents => {
      this.contactRequestService.salesAgents = agents;
    });
    this.httpService.getAllInterestLevels().subscribe(res => {
      this.interestLevels = res;
    });

    this.httpService.getAccommodationTypes().subscribe(types => this.accommodationTypes = types);
    this.httpService.getRoomTypes().subscribe(res => this.roomTypes = res);
    this.httpService.getBoardTypes().subscribe(res => this.boardTypes = res);
    this.httpService.getEmailSignaturesByUser().subscribe(res => {
      this.contactRequestService.emailSignatures = res;
      this.emailForm = this.builder.group({
        subject: "",
        body: "",
        from: null,
        sendTo: null,
        emailTemplate: null,
        signature: this.getEmailSignatureByStudentLanguage(res),
        withPastePixel: false
      });
    });
  }

  updateEmailSiguatureByStudentLanguage() {
    this.emailForm.get("signature")!.setValue(this.getEmailSignatureByStudentLanguage(this.contactRequestService.emailSignatures));
  }

  getEmailSignatureByStudentLanguage(signatures: EmailSignature[]) {
    if (!this.contactRequestService.contactRequest!.language) {
      return this.getDefaultEmailSignature(signatures);
    }
    for (let signature of signatures) {
      if (signature.language.id == this.contactRequestService.contactRequest!.language.id) {
        return signature;
      }
    }
    return this.getDefaultEmailSignature(signatures);
  }

  getDefaultEmailSignature(signatures: EmailSignature[]) {
    if (signatures.length > 0) {
      return signatures[0];
    } else {
      return null;
    }
  }

  initCommentForm() {
    this.commentForm = this.builder.group({
      body: ["", Validators.required]
    });
    if (this.contactRequestService.contactRequest != undefined && this.contactRequestService.contactRequest.isDeleted) {
      this.commentForm.controls["body"].disable();
    }
  }

  updateRequestInformation() {
    this.setRequestInformations();
    this.updateEmailSiguatureByStudentLanguage();
  }

  initContactRequestForm() {
    this.contactRequestForm = this.builder.group({
      firstName: { value: this.contactRequestService.contactRequest!.firstName, disabled: this.contactRequestService.contactRequest!.isDeleted },
      lastName: { value: this.contactRequestService.contactRequest!.lastName, disabled: this.contactRequestService.contactRequest!.isDeleted },
      phoneNumber: [{ value: this.contactRequestService.contactRequest!.phoneNumber, disabled: this.contactRequestService.contactRequest!.isDeleted }, Validators.pattern(ValidatorConstants.PHONE_NUMBER_REGEX)],
      email: { value: this.contactRequestService.contactRequest!.email, disabled: this.contactRequestService.contactRequest!.isDeleted },
      secondEmail: { value: this.contactRequestService.contactRequest!.secondEmail, disabled: this.contactRequestService.contactRequest!.isDeleted }, 
      cityCountry: { value: this.contactRequestService.contactRequest!.cityCountry, disabled: this.contactRequestService.contactRequest!.isDeleted },
      interestedIn: { value: this.contactRequestService.contactRequest!.interestedIn, disabled: this.contactRequestService.contactRequest!.isDeleted },
      language: { value: this.contactRequestService.contactRequest!.language, disabled: this.contactRequestService.contactRequest!.isDeleted },
      accommodationType: { value: this.contactRequestService.contactRequest!.accommodationType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      roomType: { value: this.contactRequestService.contactRequest!.roomType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      boardType: { value: this.contactRequestService.contactRequest!.boardType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      destination: { value: this.contactRequestService.contactRequest!.destination, disabled: this.contactRequestService.contactRequest!.isDeleted },
      needInsurance: { value: this.contactRequestService.contactRequest!.needInsurance, disabled: this.contactRequestService.contactRequest!.isDeleted },
      needGarantieAnnulation: { value: this.contactRequestService.contactRequest!.needGarantieAnnulation, disabled: this.contactRequestService.contactRequest!.isDeleted },
      otherAccommodationType: { value: this.contactRequestService.contactRequest!.otherAccommodationType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      otherRoomType: { value: this.contactRequestService.contactRequest!.otherRoomType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      otherBoardType: { value: this.contactRequestService.contactRequest!.otherBoardType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      birthDate: { value: this.contactRequestService.contactRequest!.birthDate == null ? null : moment(this.contactRequestService.contactRequest!.birthDate).toDate(), disabled: this.contactRequestService.contactRequest!.isDeleted },
      courseType: { value: this.contactRequestService.contactRequest!.courseType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      courseOption: { value: this.contactRequestService.contactRequest!.courseOption, disabled: this.contactRequestService.contactRequest!.isDeleted },
      startDate: { value: this.contactRequestService.contactRequest!.startDate == null ? null : moment(this.contactRequestService.contactRequest!.startDate).toDate(), disabled: this.contactRequestService.contactRequest!.isDeleted },
      endDate: { value: this.contactRequestService.contactRequest!.endDate == null ? null : moment(this.contactRequestService.contactRequest!.endDate).toDate(), disabled: this.contactRequestService.contactRequest!.isDeleted },
      school: { value: this.contactRequestService.contactRequest!.school, disabled: this.contactRequestService.contactRequest!.isDeleted },
      appointmentType: { value: this.contactRequestService.contactRequest!.appointmentType, disabled: this.contactRequestService.contactRequest!.isDeleted },
      country: { value: this.contactRequestService.contactRequest!.country, disabled: this.contactRequestService.contactRequest!.isDeleted },
      city: { value: this.contactRequestService.contactRequest!.city, disabled: this.contactRequestService.contactRequest!.isDeleted },
      postCode: { value: this.contactRequestService.contactRequest!.postCode, disabled: this.contactRequestService.contactRequest!.isDeleted },
    });
    this.contactRequestService.contactRequestForm = this.contactRequestForm;
    if (this.contactRequestService.contactRequest!.destination) {
      this.updateSchools(this.contactRequestService.contactRequest!.destination);
    }
  }

  setRequestInformations() {
    let contactRequest = this.contactRequestService.contactRequest;
    this.contactRequestService.contactRequest!.firstName = this.contactRequestForm.value.firstName;
    this.contactRequestService.contactRequest!.lastName = this.contactRequestForm.value.lastName;
    this.contactRequestService.contactRequest!.language = this.contactRequestForm.value.language;
    this.contactRequestService.contactRequest!.phoneNumber = this.contactRequestForm.value.phoneNumber;
    this.contactRequestService.contactRequest!.email = this.contactRequestForm.value.email;
    this.contactRequestService.contactRequest!.cityCountry = this.contactRequestForm.value.cityCountry;
    this.contactRequestService.contactRequest!.interestedIn = this.contactRequestForm.value.interestedIn;
    this.contactRequestService.contactRequest!.language = this.contactRequestForm.value.language;
    this.contactRequestService.contactRequest!.accommodationType = this.contactRequestForm.value.accommodationType;
    this.contactRequestService.contactRequest!.roomType = this.contactRequestForm.value.roomType;
    this.contactRequestService.contactRequest!.boardType = this.contactRequestForm.value.boardType;
    this.contactRequestService.contactRequest!.needInsurance = this.contactRequestForm.value.needInsurance;
    this.contactRequestService.contactRequest!.needGarantieAnnulation = this.contactRequestForm.value.needGarantieAnnulation;
    this.contactRequestService.contactRequest!.otherAccommodationType = this.contactRequestForm.value.otherAccommodationType;
    this.contactRequestService.contactRequest!.otherRoomType = this.contactRequestForm.value.otherRoomType;
    this.contactRequestService.contactRequest!.otherBoardType = this.contactRequestForm.value.otherBoardType;
    this.contactRequestService.contactRequest!.courseType = this.contactRequestForm.value.courseType;
    this.contactRequestService.contactRequest!.courseOption = this.contactRequestForm.value.courseOption;
    this.contactRequestService.contactRequest!.school = this.contactRequestForm.value.school;
    this.contactRequestService.contactRequest!.language = this.contactRequestForm.value.language;
    this.contactRequestService.contactRequest!.school = this.contactRequestForm.value.school;
    this.contactRequestService.contactRequest!.firstName = this.contactRequestForm.value.firstName;
    this.contactRequestService.contactRequest!.lastName = this.contactRequestForm.value.lastName;
    this.contactRequestService.contactRequest!.phoneNumber = this.contactRequestForm.value.phoneNumber;
    this.contactRequestService.contactRequest!.email = this.contactRequestForm.value.email;
    this.contactRequestService.contactRequest!.destination = this.contactRequestForm.value.destination;
    this.contactRequestService.contactRequest!.secondEmail = this.contactRequestForm.value.secondEmail;
    if(this.contactRequestForm.value.birthDate){
      this.contactRequestService.contactRequest!.birthDate = moment(this.contactRequestForm.value.birthDate).utc(true);
    }
    if(this.contactRequestForm.value.startDate){
      this.contactRequestService.contactRequest!.startDate = moment(this.contactRequestForm.value.startDate).utc(true);
    }
    if(this.contactRequestForm.value.endDate){
      this.contactRequestService.contactRequest!.endDate = moment(this.contactRequestForm.value.endDate).utc(true);
    }
    this.contactRequestService.contactRequest!.appointmentType = this.contactRequestForm.value.appointmentType;
    this.contactRequestService.contactRequest!.country = this.contactRequestForm.value.country;
    this.contactRequestService.contactRequest!.city = this.contactRequestForm.value.city;
    this.contactRequestService.contactRequest!.postCode = this.contactRequestForm.value.postCode;
    this.contactRequestService.contactRequest!.usersInCharge = this.contactRequestService.getCheckedUsers();
    this.httpService.updateContactRequestWithDetail(this.contactRequestService.contactRequest!).subscribe(res => {
      console.log(this.contactRequestService.contactRequest);
      this.contactRequestService.contactRequest!.versionId = res;
      this.toastService.addSuccess("Success", "Updated successfully");
      this.contactRequestService.isSendingRequest = false;
    }, error => {
      let apiError = new ApiError(error);
      this.toastService.addError("Error", apiError.message);
      this.contactRequestService.isSendingRequest = false;
    });
  }

  updateComments() {
    this.httpService.getContactRequestComments(this.contactRequestService.contactRequest!.id!).subscribe(comments => {
      this.comments = comments;
    });
  }

  postComment() {
    this.httpService.postContactRequestComment(
      new ContactRequestComment(
        this.commentForm.value.body,
        this.contactRequestService.contactRequest!.id!
      )
    ).subscribe(() => {
      console.log("success");
      this.initCommentForm();
      this.updateComments();
    });
  }

  replyByEmail() {
    this.httpService.checkGmailRefreshToken().subscribe(res => {
      if (res == true) {
        console.log(res);
        this.router.navigate(["/management/email-template", { requestId: this.contactRequestService.contactRequest!.id }]);
      } else {
        console.log("no token");
        this.noTokenError = true;
      }
    });
  }

  setDateToContact(days: number) {
    let today = moment();
    let dateToContact = today.add(days, "days").utc(true);
    this.contactRequestService.contactRequest!.dateToContact = dateToContact;
    this.contactRequestService.contactRequest!.isProcessed = false;
    this.httpService.updateContactRequest(this.contactRequestService.contactRequest!).subscribe(() => {
      this.toastService.addSuccess("Success", "Relance set to: " + dateToContact.format("DD/MM/YYYY"));
    }, error => {
      this.toastService.addError("Error !", error.message);
    });
  }

  activateEmailDialog() {
    this.displayMailingDialog = true;
    this.httpService.getLeadEmailTemplatesByLanguage(this.contactRequestService.contactRequest!.language).subscribe(res => this.emailTemplates = res);
  }

  closeEmailModal() {
    this.displayMailingDialog = false;
  }


  sendEmailToClient() {
    this.emailSending = true;
    let message = new GmailMessage(
      [this.contactRequestService.contactRequest!.email],
      this.emailForm.value.subject,
      this.emailForm.value.body
    );
    this.httpService.sendHtmlEmailWithContactRequestQuotations(
      this.gmailService.getFormData(message, this.attachmentQuotations),
      this.contactRequestService.contactRequest!.id!,
      this.emailForm.value.signature ? this.emailForm.value.signature.id : null,
      this.emailForm.value.withPastePixel
    ).subscribe(() => {
      this.toastService.addSuccess("Success", "Email sent");
      this.displayMailingDialog = false;
      this.emailSending = false;
    }, error => {
      console.error(error);
      this.toastService.addError("Error", error.message);
      this.emailSending = false;
    });
  }


  setCustomDateToContact() {
    let customDate = moment(this.selectedDate).utc(true);
    this.contactRequestService.contactRequest!.dateToContact = customDate;
    this.contactRequestService.contactRequest!.isProcessed = false;
    this.httpService.updateContactRequest(this.contactRequestService.contactRequest!).subscribe(() => {
      this.toastService.addSuccess("Success", "Relance set to: " + customDate.format("DD/MM/YYYY"));
    }, error => {
      this.toastService.addError("Error !", error.message);
    });
  }



  copyEmailToClipboard() {
    const el = document.createElement("textarea");
    el.value = this.contactRequestService.contactRequest!.email;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    this.toastService.addSuccess("Success", "Email " + this.contactRequestService.contactRequest!.email + " copied to clipboard");
  }

  copyToClipboard() {
    const el = document.createElement("textarea");
    el.value = this.contactRequestService.contactRequest!.phoneNumber;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    this.toastService.addSuccess("Success", "Phone number " + this.contactRequestService.contactRequest!.phoneNumber + " copied to clipboard");
  }

  callPhoneNumber() {
    if (!this.contactRequestService.contactRequest!.phoneNumber) {
      this.toastService.addError("Error", "There is no phone number to call");
      return;
    }
    let number = this.contactRequestService.contactRequest!.phoneNumber;
    let a = document.createElement("a");
    a.setAttribute("href", "tel:" + number);
    a.click();
  }

  restoreRequest() {
    this.httpService.restoreWpContactRequest(this.contactRequestService.contactRequest!.id!).subscribe(() => {
      this.router.navigateByUrl("/management/leads");
    });
  }

  interestLevelChange(event: any) {
    this.contactRequestService.contactRequest!.interestLevel = event;
    this.httpService.updateContactRequest(this.contactRequestService.contactRequest!).subscribe(() => {
      this.toastService.addSuccess("Success", "Interest level set to " + this.contactRequestService.contactRequest!.interestLevel.name);
    }, error => {
      this.toastService.addError("Error", error.message);
    });
  }

  updateProcessStatus(option: any) {
    if (option.value == true) {
      if (this.validateStatus() == false) {
        setTimeout(() => {
          this.contactRequestService.requestForm.get("processStatus")!.setValue(false);
        }, 100);
        return;
      }
    }
    if (this.checkIfValueChanges(option) == true) {
      this.contactRequestService.contactRequest!.isProcessed = option.value;
      this.httpService.updateLeadProcessedStatus(option.value, this.contactRequestService.contactRequest!.id!)
        .subscribe(() => this.toastService.addSuccess("Success", "status updated"), error => this.toastService.addError("Error", error.message));
    }
  }

  checkIfValueChanges(option: any) {
    if (option.value == this.contactRequestService.requestForm.value.processStatus) {
      return false;
    } else {
      return true;
    }
  }

  validateStatus() {
    let response = true;
    if (this.contactRequestService.contactRequest!.interestLevel == null) {
      this.toastService.addError("Error", "You have to set 'hot'/'warm'/'cold'");
      response = false;
    }
    if (this.contactRequestService.contactRequest!.usersInCharge!.length == 0) {
      this.toastService.addError("Error", "You have to assign at least one person.");
      response = false;
    }
    return response;
  }

  updateAgeAtStartDate(value: Date) {
    if (this.contactRequestForm.value.startDate && this.contactRequestForm.value.birthDate) {
      let startDate = moment(this.contactRequestForm.value.startDate);
      let birthDate = moment(this.contactRequestForm.value.birthDate);
      this.contactRequestService.contactRequest!.ageAtStartDate = startDate.diff(birthDate, "years");
    } else {
      this.contactRequestService.contactRequest!.ageAtStartDate = null;
    }
  }

  updateQuotationRequestDuration(value: Date) {
    if (this.contactRequestForm.value.startDate && this.contactRequestForm.value.endDate) {
      let startDate = moment(this.contactRequestForm.value.startDate);
      let endDate = moment(this.contactRequestForm.value.endDate);
      this.contactRequestService.contactRequest!.duration = Math.round(endDate.diff(startDate, "weeks", true));
    } else {
      this.contactRequestService.contactRequest!.duration = null;
    }
  }

  updateSchools(selectedCountry: Country) {
    if(!selectedCountry && !this.contactRequestForm.value.school){
      this.contactRequestService.filteredCountries = this.contactRequestService.countries;
      this.contactRequestService.filteredSchools = this.contactRequestService.schools;
      return;
    } else if(!selectedCountry){
      return;
    }
    this.contactRequestService.filteredSchools = [];
    for (let school of this.contactRequestService.schools) {
      for (let countryId of school.countries) {
        if (selectedCountry.id === countryId) {
          this.contactRequestService.filteredSchools.push(school);
        }
      }
    }
  }

  clearSelectedSchool() {
    this.contactRequestForm.get("school")!.setValue(null);
    this.contactRequestService.programmeInformations = [];
  }

  insertLeadEmailTemplate(template: LeadEmailTemplate) {
    this.httpService.getComplateContactRequestEmailTemplate(template, this.contactRequestService.contactRequest!.id!).subscribe(res => {
      this.emailForm.get("subject")!.setValue(res.subject);
      this.emailForm.get("body")!.setValue(res.body);
    }, error => {
      this.toastService.addError("Template loading error", error.message);
    });
  }

  loadQuotation() {
    this.displayQuotationSelection = true;
    this.quotations = [];
    this.httpService.getContactRequestQuotationByRequestId(this.contactRequestService.contactRequest!.id!).subscribe(res => {
      this.quotations = res;
    });
  }

  loadSelectedQuotation() {
    this.attachmentLoading = true;
    this.displayQuotationSelection = false;
    let quotationId = this.selectedQuotation!.id;
    this.httpService.getContactRequestQuotationPdfById(quotationId!, this.contactRequestService.contactRequest!.language.name).subscribe(res => {
      this.attachmentQuotations.push(new AttachmentQuotation(quotationId!, new File([res], this.selectedQuotation!.description + "_" + this.contactRequestService.contactRequest!.lastName + ".pdf")));
      this.attachmentLoading = false;
      this.selectedQuotation = null;
    }, error => {
      this.toastService.addError("Quotation loading error", error.message);
      this.attachmentLoading = false;
      this.selectedQuotation = null;
    });
  }

  getObjectURL(file: File) {
    return this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  removeAttachmentFile(file: File) {
    for (let i = 0; i < this.attachmentQuotations.length; i++) {
      if (this.attachmentQuotations[i].file === file) {
        this.attachmentQuotations.splice(i, 1);
      }
    }
  }

  activateEmailHistoryDetail(selectedEmail: ContactRequestEmailHistory) {
    this.selectedEmailHistory = selectedEmail;
    this.displayMailHistoryDetail = true;
  }

  updateLviUrl(school: School) {
    if (school) {
      this.contactRequestService.programmeInformations = school.programmeInformationList;
    }
  }

  languageChange(language: Language) {
    if (this.contactRequestService.contactRequestForm.value.school) {
      this.httpService.getProgrammeInformationsBySchoolAndLanguage(this.contactRequestService.contactRequestForm.value.school.id, language.id).subscribe(res => {
        this.contactRequestService.programmeInformations = res;
      });
    }
  }

  updateDestination(school: SimpleSchoolWithCountries) {
    if (!school) {
      return;
    }
    this.contactRequestService.filteredCountries = [];
    for (let destination of this.contactRequestService.countries) {
      for (let countryId of school.countries) {
        if (destination.id === countryId) {
          this.contactRequestService.filteredCountries.push(destination);
        }
      }
    }
  }

  onSchoolClear(){
    this.contactRequestService.filteredCountries = this.contactRequestService.countries;
  }
}